import type { ReactNode } from "react";
import { createContext, useContext, useState, useCallback, useMemo } from "react";
import type { TeacherComment, ResolvedHighlight } from "@markeroo/shared";
import type { HighlightTag } from "../unified";
import type {
  HighlightNavigationContextValue,
  HighlightWithMetadata,
  HighlightSource,
} from "../types/highlightNavigation";
import { ui } from "@/strings";

export const HighlightNavigationContext = createContext<HighlightNavigationContextValue | null>(null);

interface HighlightNavigationProviderProps {
  children: ReactNode;
  tags: HighlightTag[];
  teacherComments: TeacherComment[];
  completedHighlightIds: string[];
  onCompletedHighlightsChange: (ids: string[]) => void;
  isTeacherView?: boolean;
  initialShowCompleted?: boolean;
  /**
   * Resolved highlight data including body hash at resolution time.
   * Used to determine if unresolve is available (body hash must match current).
   */
  resolvedHighlightsData?: ResolvedHighlight[];
  /**
   * Current document body hash for unresolve comparison.
   * If this matches bodyHashAtResolution, the highlight can be unresolved.
   */
  currentBodyHash?: string;
}

function createSortKey(startPath: number[], startChar: number): string {
  return `${startPath.join("-")}-${startChar}`;
}

function createHighlightId(
  tag: HighlightTag | TeacherComment,
  type: HighlightSource,
  startPath: number[],
  startChar: number,
): string {
  const tagId = type === "ai" ? (tag as HighlightTag).tag_id : `TEACHER_COMMENT_${(tag as TeacherComment).id}`;
  return `${tagId}-${startPath.join(",")}-${startChar}`;
}

export function HighlightNavigationProvider({
  children,
  tags,
  teacherComments,
  completedHighlightIds,
  onCompletedHighlightsChange,
  isTeacherView = false,
  initialShowCompleted = false,
  resolvedHighlightsData = [],
  currentBodyHash = "",
}: HighlightNavigationProviderProps) {
  const [selectedHighlightId, setSelectedHighlightId] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState<boolean>(
    isTeacherView ? true : initialShowCompleted,
  );
  const [hoveredLegendKey, setHoveredLegendKey] = useState<string | null>(null);

  // Build a map of highlight ID to body hash at resolution for O(1) lookups
  const resolvedHighlightsMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const resolved of resolvedHighlightsData) {
      map.set(resolved.highlightId, resolved.bodyHashAtResolution);
    }
    return map;
  }, [resolvedHighlightsData]);

  // Build base list of highlights WITHOUT completion status (memoized on tags/comments only)
  // This prevents re-creating and re-sorting the entire list when only completion status changes
  const baseHighlights = useMemo<Omit<HighlightWithMetadata, "isCompleted">[]>(() => {
    const highlights: Omit<HighlightWithMetadata, "isCompleted">[] = [];

    // Process AI highlights (from tags)
    for (const tag of tags) {
      const highlightId = createHighlightId(tag, "ai", tag.startPath, tag.startChar);
      const sortKey = createSortKey(tag.startPath, tag.startChar);

      highlights.push({
        id: highlightId,
        tag,
        type: "ai",
        sortKey,
        phrase: tag.phrase || "",
        explanation: tag.explanation || "",
        // eslint-disable-next-line no-restricted-syntax -- User-selected highlight color from palette
        color: tag.color || "#fbbf24",
        label: tag.label || tag.tag_id,
        suggestion: tag.suggestion || null,
        tagId: tag.tag_id,
      });
    }

    // Process teacher comments
    for (const comment of teacherComments) {
      const highlightId = createHighlightId(
        comment,
        "teacher",
        comment.startPath,
        comment.startChar,
      );
      const sortKey = createSortKey(comment.startPath, comment.startChar);

      highlights.push({
        id: highlightId,
        tag: comment,
        type: "teacher",
        sortKey,
        phrase: comment.phrase || "",
        explanation: comment.comment_text,
        // eslint-disable-next-line no-restricted-syntax -- User-selected teacher comment color
        color: comment.color || "#64748b",
        label: ui.highlightNavigation.teacherCommentLabel,
        suggestion: null,
        teacherName: comment.teacher_name,
        createdBy: comment.created_by,
      });
    }

    // Sort by document position (startPath, then startChar)
    highlights.sort((a, b) => {
      const pathA = a.sortKey.split("-").map(Number);
      const pathB = b.sortKey.split("-").map(Number);

      // Compare paths element by element
      const minLen = Math.min(pathA.length - 1, pathB.length - 1); // -1 for startChar
      for (let i = 0; i < minLen; i++) {
        if (pathA[i] !== pathB[i]) {
          return pathA[i] - pathB[i];
        }
      }

      // If paths are equal, compare by startChar (last element)
      return pathA[pathA.length - 1] - pathB[pathB.length - 1];
    });

    return highlights;
  }, [tags, teacherComments]);

  // Enrich base highlights with completion status (only recomputes when status changes)
  const orderedHighlights = useMemo<HighlightWithMetadata[]>(() => {
    // Convert to Set once for O(1) lookups instead of O(n) array includes
    const completedSet = new Set(completedHighlightIds);

    return baseHighlights.map((highlight) => ({
      ...highlight,
      isCompleted: completedSet.has(highlight.id),
    }));
  }, [baseHighlights, completedHighlightIds]);

  // Highlights included in keyboard navigation and student sidebar view
  const navigableHighlights = useMemo<HighlightWithMetadata[]>(() => {
    if (isTeacherView || showCompleted) {
      return orderedHighlights;
    }
    return orderedHighlights.filter((h) => !h.isCompleted);
  }, [orderedHighlights, isTeacherView, showCompleted]);

  // Get current index of selected highlight
  const currentIndex = useMemo(() => {
    if (!selectedHighlightId) return null;
    return navigableHighlights.findIndex((h) => h.id === selectedHighlightId);
  }, [selectedHighlightId, navigableHighlights]);

  // Selection handlers
  const selectHighlight = useCallback((id: string) => {
    setSelectedHighlightId(id);
  }, []);

  const deselectHighlight = useCallback(() => {
    setSelectedHighlightId(null);
  }, []);

  // Completion toggle
  const toggleComplete = useCallback(
    (id: string) => {
      const isCurrentlyCompleted = completedHighlightIds.includes(id);

      // If marking as incomplete, just remove from completed set
      if (isCurrentlyCompleted) {
        const newCompletedIds = completedHighlightIds.filter((hid) => hid !== id);
        onCompletedHighlightsChange(newCompletedIds);
        return;
      }

      const newCompletedIds = [...completedHighlightIds, id];

      // For students, when resolving a highlight, automatically move selection
      // to the next unresolved highlight if one exists. If none remain, clear selection.
      if (!isTeacherView) {
        const unresolvedAfter = orderedHighlights.filter(
          (h) => !newCompletedIds.includes(h.id),
        );

        if (unresolvedAfter.length === 0) {
          setSelectedHighlightId(null);
        } else {
          const currentIndexInOrdered = orderedHighlights.findIndex((h) => h.id === id);
          let nextUnresolved: HighlightWithMetadata | undefined;

          if (currentIndexInOrdered !== -1) {
            // Look forward from the current highlight
            for (let i = currentIndexInOrdered + 1; i < orderedHighlights.length; i++) {
              const candidate = orderedHighlights[i];
              if (!newCompletedIds.includes(candidate.id)) {
                nextUnresolved = candidate;
                break;
              }
            }

            // If none ahead, wrap to the beginning
            if (!nextUnresolved) {
              for (let i = 0; i < currentIndexInOrdered; i++) {
                const candidate = orderedHighlights[i];
                if (!newCompletedIds.includes(candidate.id)) {
                  nextUnresolved = candidate;
                  break;
                }
              }
            }
          }

          setSelectedHighlightId(nextUnresolved ? nextUnresolved.id : null);
        }
      }

      onCompletedHighlightsChange(newCompletedIds);
    },
    [completedHighlightIds, isTeacherView, onCompletedHighlightsChange, orderedHighlights],
  );

  // Navigation handlers
  const navigateNext = useCallback(() => {
    if (navigableHighlights.length === 0) return;

    if (currentIndex === null) {
      // No selection - select first highlight
      setSelectedHighlightId(navigableHighlights[0].id);
    } else {
      // Select next (wrap around to first)
      const nextIndex = (currentIndex + 1) % navigableHighlights.length;
      setSelectedHighlightId(navigableHighlights[nextIndex].id);
    }
  }, [navigableHighlights, currentIndex]);

  const navigatePrevious = useCallback(() => {
    if (navigableHighlights.length === 0) return;

    if (currentIndex === null) {
      // No selection - select last highlight
      setSelectedHighlightId(navigableHighlights[navigableHighlights.length - 1].id);
    } else {
      // Select previous (wrap around to last)
      const prevIndex =
        currentIndex === 0 ? navigableHighlights.length - 1 : currentIndex - 1;
      setSelectedHighlightId(navigableHighlights[prevIndex].id);
    }
  }, [navigableHighlights, currentIndex]);

  // Check if a resolved highlight can be unresolved
  // Only possible if document body hash matches the hash at resolution time
  const canUnresolve = useCallback(
    (highlightId: string): boolean => {
      // Must be a resolved highlight with stored hash data
      const storedHash = resolvedHighlightsMap.get(highlightId);
      if (!storedHash) {
        return false;
      }
      // Can only unresolve if document hasn't changed since resolution
      return storedHash === currentBodyHash && currentBodyHash !== "";
    },
    [resolvedHighlightsMap, currentBodyHash]
  );

  // Memoize Set to avoid creating a new object on every render
  const completedHighlightIdsSet = useMemo(
    () => new Set(completedHighlightIds),
    [completedHighlightIds]
  );

  const value = useMemo<HighlightNavigationContextValue>(
    () => ({
      selectedHighlightId,
      completedHighlightIds: completedHighlightIdsSet,
      orderedHighlights,
      currentIndex,
      selectHighlight,
      deselectHighlight,
      toggleComplete,
      navigateNext,
      navigatePrevious,
      showCompleted,
      setShowCompleted,
      hoveredLegendKey,
      setHoveredLegendKey,
      canUnresolve,
    }),
    [
      selectedHighlightId,
      completedHighlightIdsSet,
      orderedHighlights,
      currentIndex,
      selectHighlight,
      deselectHighlight,
      toggleComplete,
      navigateNext,
      navigatePrevious,
      showCompleted,
      setShowCompleted,
      hoveredLegendKey,
      setHoveredLegendKey,
      canUnresolve,
    ],
  );

  return (
    <HighlightNavigationContext.Provider value={value}>
      {children}
    </HighlightNavigationContext.Provider>
  );
}

export function useHighlightNavigation(): HighlightNavigationContextValue {
  const context = useContext(HighlightNavigationContext);
  if (!context) {
    throw new Error("useHighlightNavigation must be used within HighlightNavigationProvider");
  }
  return context;
}
